import { useEffect, useState, type ReactNode } from "react";
import { Button } from "./ui";
import {
  dismissToast, isOffline, subscribeToasts, timeOfDay,
  type SaveState, type ToastItem,
} from "../lib/saving";

/**
 * What a save looks like. The logic is in lib/saving.ts; read the note there.
 *
 *   const save = useSaveState({ dirty, what: "this listing" });
 *   <SaveBar save={save} onSave={() => save.run(write)} />
 *
 * SaveBar is the Save button with its state beside it and the failure under
 * it. SaveStatus is the state alone, for an instant action or for a form that
 * closes after saving. SaveError is the failure alone.
 */

const LABEL = {
  saving: "Saving…",
  failed: "Not saved",
  dirty: "Unsaved changes",
} as const;

export function SaveStatus({ save, idle, savedLabel = "Saved", hideWhenIdle = false }: {
  save: SaveState;
  /** What to say before anything has been edited or saved. */
  idle?: string;
  /** "Saved", "Recorded", "Uploaded" — the time and tick are added. */
  savedLabel?: string;
  /** For instant actions: say nothing until something happens. */
  hideWhenIdle?: boolean;
}) {
  const { status, savedAt } = save;
  if (status === "clean" && (hideWhenIdle || !idle)) return null;
  const text = status === "saved" && savedAt ? `${savedLabel} ✓ ${timeOfDay(savedAt)}`
    : status === "clean" ? idle
    : status === "saved" ? `${savedLabel} ✓`
    : LABEL[status];
  return (
    <span className={`savestate ${status}`} role="status" aria-live="polite">
      {text}
    </span>
  );
}

export function SaveError({ save, onRetry, hint }: {
  save: SaveState;
  /** Offered as "Try again" beside the message. */
  onRetry?: () => void;
  /** What is still true after the failure. */
  hint?: string;
}) {
  if (!save.error) return null;
  const keep = hint ?? "Your changes are still here — press Save to try again.";
  return (
    <div className="errnote savefail" role="alert">
      <p>
        <strong>Not saved.</strong> {save.error}
        {isOffline() && " Reconnect, then try again."}
      </p>
      <p className="savefail-keep">{keep}</p>
      <div className="savefail-actions">
        {onRetry && (
          <button type="button" className="linkbtn" disabled={save.saving}
                  onClick={onRetry}>
            Try again
          </button>
        )}
        <button type="button" className="linkbtn" onClick={save.dismiss}>Dismiss</button>
      </div>
    </div>
  );
}

/**
 * The Save button, with what has happened to the work beside it.
 *
 * Highlighted (primary) while there is something to save or the last attempt
 * failed; quiet otherwise. Disabled only while a write is in flight, so it
 * cannot be pressed twice. `children` go before the button — Cancel, usually.
 */
export function SaveBar({
  save, onSave, label = "Save", savingLabel = "Saving…", savedLabel, idle,
  disabled = false, alwaysPrimary = false, retryHint, children,
}: {
  save: SaveState;
  onSave: () => void;
  label?: string;
  savingLabel?: string;
  savedLabel?: string;
  idle?: string;
  disabled?: boolean;
  /** For a create form, where the button is the point of the page. */
  alwaysPrimary?: boolean;
  retryHint?: string;
  children?: ReactNode;
}) {
  const hot = alwaysPrimary || save.dirty || save.status === "failed";
  return (
    <>
      <div className="savebar">
        <SaveStatus save={save} idle={idle} savedLabel={savedLabel} />
        <div className="savebar-actions">
          {children}
          <Button type="button" variant={hot ? "primary" : "ghost"}
                  onClick={onSave} disabled={disabled || save.saving}
                  aria-busy={save.saving}>
            {save.saving ? savingLabel : label}
          </Button>
        </div>
      </div>
      <SaveError save={save} onRetry={onSave} hint={retryHint} />
    </>
  );
}

/** Mounted once, in App. Shows toastSaved() confirmations. */
export function Toaster() {
  const [items, setItems] = useState<ToastItem[]>([]);
  useEffect(() => subscribeToasts(setItems), []);
  return (
    <div className="toasts" role="status" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={`toast ${t.tone}`}>
          <span>{t.text}</span>
          <button type="button" aria-label="Dismiss" onClick={() => dismissToast(t.id)}>✕</button>
        </div>
      ))}
    </div>
  );
}
